<template>
  <div
    id="map"
    :class="['management-workspace-map']"
    type="light"
    oncontextmenu="return false"
  >
    <div class="management-map-buttons">
      <div v-if="showZoomButtons" class="buttons-container">
        <custom-button
          icon="add"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="buttonStyling"
          @buttonClick="zoomIn"
        />
        <custom-button
          icon="remove"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="{ padding: '8px', borderRadius: '4px' }"
          @buttonClick="zoomOut"
        />
      </div>
      <div v-if="showCenterMapButton" class="buttons-container">
        <custom-button
          icon="location_searching"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="buttonStyling"
          :disabled="!mapHasFeatures"
          @buttonClick="centerMap"
        />
      </div>
      <div v-if="showCoordinatesButton" class="buttons-container hoverable">
        <custom-button
          v-if="!showCoordinatesInputs"
          icon="location_on"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="buttonStyling"
          :label="$tc('Coordinate', 2)"
          showLabelOnHover
          @buttonClick="openCoordinatesInputs"
        />
        <div v-else class="coordinates-inputs-container">
          <v-icon
            :size="12"
            :color="'#FFFFFF'"
            @click="closeCoordinatesInputs"
          >
            location_on
          </v-icon>
          <input-field
            :key="'latitude'"
            :input-type="'float'"
            :value="coordinates.latitude"
            :placeholder="$t('Latitude')"
            :min="-90"
            :max="90"
            :size="'medium'"
            :inputFieldContainerStyling="coordinatesInputsContainerStyling"
            :inputStyle="coordinatesInputsStyling"
            @input="coordinates.latitude = $event"
          />
          <input-field
            :key="'longitude'"
            :input-type="'float'"
            :value="coordinates.longitude"
            :placeholder="$t('Longitude')"
            :min="-180"
            :max="180"
            :size="'medium'"
            :inputFieldContainerStyling="coordinatesInputsContainerStyling"
            :inputStyle="coordinatesInputsStyling"
            @input="coordinates.longitude = $event"
          />
          <v-icon
            :size="12"
            :color="'#FFFFFF'"
            @click="centerOnCoordinates"
          >
            done
          </v-icon>
        </div>
      </div>
      <div v-if="showUploadGeomFromFileButton" class="buttons-container">
        <custom-button
          icon="cloud_upload"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="buttonStyling"
          :label="$t('AreaFromFile')"
          showLabelOnHover
          @buttonClick="openUploadGeomFileDialog"
        />
      </div>
      <div v-if="showRestartButton" class="buttons-container">
        <custom-button
          icon="restart_alt"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="buttonStyling"
          :label="$t('Restart')"
          :disabled="!featureIsDrawn"
          showLabelOnHover
          @buttonClick="resetPolygon"
        />
      </div>
      <div v-if="showAllWorkspacesButton" class="buttons-container">
        <custom-button
          icon="mdi-eye"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="buttonStyling"
          :label="$t('ViewAllWorkspaces')"
          showLabelOnHover
          @buttonClick="showHideAllWorkspacesMethod"
        />
      </div>
    </div>
    <dialog-content :isOpen="uploadGeomFileDialogIsOpen" :isPersistent="true" :onClose="closeUploadGeomFileDialog" :card-style="{ 'display': 'table' }">
      <template v-slot:content>
        <div class="tabs-custom">
          <Tabs
            :tabs="uploadFileDialogTabs"
            :activeTab="0"
            :center-tabs="false"
          />
        </div>
        <div class="tabs-content-custom">
          <InputField
            :inputStyle="{
              backgroundColor: 'white',
            }"
            :inputFieldContainerStyling="uploadFileInputContainersStyle"
            :inputType="'select'"
            :value="fileType"
            :select-options-list="fileTypeOptions"
            :label="$tc('Type', 1)"
            required
            size="large"
            @input="fileType = $event"
          />
          <InputField
            :inputStyle="{
              backgroundColor: 'white',
            }"
            :inputFieldContainerStyling="uploadFileInputContainersStyle"
            :input-type="'select'"
            :value="fileProjection"
            :select-options-list="fileProjectionOptions"
            :label="$tc('Projection', 1)"
            required
            size="large"
            @input="fileProjection = $event"
          />
          <InputField
            ref="fileInput"
            :inputStyle="{
              color: '#767676 !important',
            }"
            :inputFieldContainerStyling="uploadFileInputContainersStyle"
            inputType="file"
            size="large"
            :fileTypes="acceptedFileTypes"
            :value="file"
            required
            @input="file = $event"
          />
          <div class="buttons-container-custom">
            <CustomButton
              :label="$t('Cancel')"
              :button-color="'#FFF'"
              :label-color="'var(--app-primary-base)'"
              :buttonStyling="uploadFileCancelButtonStyle"
              @buttonClick="closeUploadGeomFileDialog"
            />
            <CustomButton
              :label="$t('Submit')"
              :button-styling="uploadFileCancelButtonStyle"
              :disabled="uploadFileSubmitButtonIsDisabled"
              @buttonClick="submitForm"
            />
          </div>
        </div>
      </template>
    </dialog-content>
    <LoadingSpinner :active.sync="loading" :is-full-page="true" color="var(--app-primary-base)" />
  </div>
</template>

<script>
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import { mapActions } from "vuex";
import { managementMapsMixin } from "@/mixins/management/managementMapsMixin";
import { GeoJSON, WKT } from "ol/format";
import { Draw, Modify, Snap } from "ol/interaction";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Icon, Style, Fill, Circle} from "ol/style";
import { Point } from "ol/geom.js";
import { Feature } from "ol";
import { defaults } from "ol/interaction";
import { transform } from "ol/proj";
import { bus } from "@/main.js";
import Map from "ol/Map";
import utils from "@/utils";
import CustomButton from '@/newComponents/_Atoms/CustomButton.vue';
import InputField from "@/newComponents/_Atoms/InputField.vue";
import DialogContent from "@/newComponents/_Molecules/DialogContent.vue";
import Tabs from "@/newComponents/_Atoms/Tabs.vue";
import LoadingSpinner from "vue-loading-overlay";
import { feature } from '@turf/turf';

export default {
  name: "ManagementMap",

  components: {
    CustomButton,
    InputField,
    DialogContent,
    LoadingSpinner,
    Tabs,
  },

  mixins: [managementMapsMixin, checkPermissionsMixin],

  props: {

    geo_json: {
      type: String,
      default: null
    },

    /**
     * Indicates if the user can draw on the map
     */
    drawable: {
      type: Boolean,
      default: false,
    },

    /**
     * Indicates if the buttons to zoom are visible
     */
    showZoomButtons: {
      type: Boolean,
      default: true,
    },

    /**
     * Indicates if the button to center the map is visible
     */
    showCenterMapButton: {
      type: Boolean,
      default: true,
    },

    /**
     * Indicates if the button to input coordinates is visible
     */
    showCoordinatesButton: {
      type: Boolean,
      default: false,
    },

    /**
     * Indicates if the button to upload a geom is visible
     */
    showUploadGeomFromFileButton: {
      type: Boolean,
      default: false,
    },

    /**
     * Indicates if the button to clear the map is visible
     */
    showRestartButton: {
      type: Boolean,
      default: false,
    },

    showAllWorkspacesButton: {
      type: Boolean,
    },

    isDeleteLayers: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // styling for the map buttons
      buttonStyling: {
        'padding': '4px !important',
        'border-radius': '4px',
        'drop-shadow': '0.85px 0.85px 1.75px rgba(0, 0, 0, 0.25)',
      },

      showCoordinatesInputs: false, // indicates if the coordinates inputs are visible (true when the user clicks on the coordinates button)
      coordinates: {
        latitude: 0, // latitude the user input
        longitude: 0, // longitude the user input
      },

      // styling for the coordinates inputs container
      coordinatesInputsContainerStyling: {
        'padding': '0px',
        'border': 'none',
        'height': '20px !important'
      },

      // styling for the coordinates inputs
      coordinatesInputsStyling: {
        'height': '20px',
        'color': '#FFFFFF',
        'background-color': 'var(--app-primary-tint-500)',
      },

      // styling for the upload file container
      uploadFileInputContainersStyle: {
        'padding': '4px 0px',
      },

      // styling for the cancel button of the upload file dialog
      uploadFileCancelButtonStyle: {
        'border': '1px solid var(--app-primary-base)',
        'padding': '4px 10px',
        'border-radius': '4px',
      },
      showAllWorkspaces: false,
      listOfWorkspaces: undefined,
      drawInteraction: undefined, // the interaction for drawing the features
      modifyInteraction: undefined, // the interaction for modifying drawn features
      featureIsDrawn: false, // indicates if the user has drawn a feature on the map
      mapHasFeatures: false, // indicates if there is any feature on the map

      snap: undefined,
      snapSource: null,

      pointLayer: null, // Layer where the location pin is added
      pointLayerSource: null, // Source for the layer where the location pin is added

      featureWKT: null,
      userDrawnFeature: null,
      markPointFeature: null, // location pin feature for centering on coordinates input by the user (@/assets/map_icons/location.png)

      uploadGeomFileDialogIsOpen: false,
      file: null,
      fileType: null,
      fileProjection: null,
      acceptedFileTypes: [".zip", ".rar", ".kml"],
      uploadFileDialogTabs: [{ label: this.$i18n.t('UploadFile')}],
      fileProjectionOptions: [],
      fileTypeOptions: [
        { id: "shape", label: "Shape" },
        { id: "kml", label: "KML" }
      ],

      loading: false,

      // Styles for the different types of features
      mapFeaturesStyle: {
        point: {
          radius: 15, // radius of the circle shown on the map
          strokeWidth: 2, // width of the outside line of the circle
          fillColor: "var(--app-primary-tint-500)", // color of the inside of the circle
        },
        polygon: {
          strokeWidth: 2, // width of the outside line of the polygon
          strokeColor: "var(--app-primary-base)", // color of the outside line of the polygon
          fillColor: "rgba(250, 250, 250, 0.5)", // color of the inside of the polygon
        },
        line: {
          strokeWidth: 4, // width of the line shown on the map
          strokeColor: "var(--app-primary-tint-500)", // color of the line
        },
      },
      selectedIds: []
    };
  },

  computed: {
    uploadFileSubmitButtonIsDisabled() {
      return !this.file || !this.fileType || !this.fileProjection;
    }
  },

  watch:{
    featureWKT(){
      this.$emit("update-map-data",this.featureWKT);
    }
  },

  mounted() {
    this.createDefaultStyle(); // styling for the user drawable features
    this.createMap();
    this.initializeMarkPoint();
    this.createPointLayer(); // Layer where the location pin is added
    this.vectorLayer.setZIndex(3);
    this.pointLayer.setZIndex(4);

    this.loadWorkspaces();
    if (this.drawable) {
      this.createDrawInteraction();
      this.createModifyInteraction();
      this.createSnapInteraction();
      this.drawInteraction.setActive(true);
    }
    this.loadProjections();

    bus.$on("draw-features", (features) => this.addFeaturesToPointLayer(features));

    bus.$on("draw-features-highlight", (features) => this.hightlightAddFeaturesToPointLayer(features));

    bus.$on("zoom-out-map", (features) => this.zoomOutMap());

    // Listen for highlight event
    bus.$on("highlight-features", (highlightData) => this.highlightFeatures(highlightData));

    bus.$on("draw-workspace", (geo_json) => this.drawWorkspace(geo_json));

    bus.$on("clear-selected-ids", () => this.clearSelectedIds());

    bus.$on("clear-vector-source-layer", () => this.clearVectorSource());

    bus.$on("set-drawable", () => {
      this.createDrawInteraction();
      this.createModifyInteraction();
      this.createSnapInteraction();
      this.modifyInteraction.setActive(true);
    });

    bus.$on("reset-polygon",() => {
      this.resetPolygon();
    });

    bus.$on("show-hide-workspaces",() => {
      this.showHideAllWorkspacesMethod();
    });

    bus.$on("upload-file-dialog",() => {
      this.openUploadGeomFileDialog();
    });

    bus.$on("set-undrawable",() => {
      this.drawInteraction.setActive(false);
      this.modifyInteraction.setActive(false);
    });
    
    bus.$on("zoom-out-map", () => this.zoomOutMap());

    bus.$on("clear-map-has-features", () => this.clearMapHasFeatures());
  },

  destroyed() {
    bus.$off("draw-features");
    bus.$off("draw-features-highlight");
    bus.$off("highlight-features");
    bus.$off("draw-workspace");
    bus.$off("clear-selected-ids");
    bus.$off("clear-vector-source-layer");
    bus.$off("set-drawable");
    bus.$off("reset-polygon");
    bus.$off("show-hide-workspaces");
    bus.$off("upload-file-dialog");
    bus.$off("set-undrawable");
    bus.$off("zoom-out-map");
    bus.$off("clear-map-has-features");
  },

  methods: {
    ...mapActions([
      "GET_LIST",
      "CREATE_IMPORT_VALIDATION",
      "CREATE_DELETE_VALIDATION",
      "fetchAllWorkspaces",
      "clearWorkspace",
      "fetchWorkspaces"
    ]),

    // overrides the createMap method in the managementMapsMixin by removing the default zoom buttons
    createMap() {
      this.getRoadMap();
      this.createVectorLayer(); // Layer where the user can draw polygons
      this.createMapView();

      this.map = new Map({
        target: "map",
        layers: [this.roadMap, this.vectorLayer],
        interactions: defaults({ doubleClickZoom: false }),
        view: this.mapView,
        controls: [] // removes the default zoom buttons
      });
    },

    loadWorkspaces() {
      this.fetchWorkspaces(
        null,null
      )
        .then(workspaces => {
          this.listOfWorkspaces = workspaces.data;

        })
        .catch(error => {
          let errorHandle = utils.errorHandler(error);
          if (errorHandle && typeof errorHandle === "string") {
            this.$toasted.global.toast_error(errorHandle);
          }
        })
    },

    /**
     * Creates the feature for the location pin used on Center on Coordinates (@/assets/map_icons/location.png)
     */
    initializeMarkPoint() {
      let iconFeature = new Feature({
        geometry: new Point([0, 0])
      });

      let iconStyle = new Style({
        image: new Icon({
          anchor: [20, 40],
          anchorXUnits: "pixels",
          anchorYUnits: "pixels",
          opacity: 1,
          src: require("@/assets/map_icons/location.png")
        }),
        zIndex: 2
      });

      iconFeature.setStyle(iconStyle);
      this.markPointFeature = iconFeature;
    },

    createDrawInteraction() {
      this.drawInteraction = new Draw({
        source: this.vectorSource,
        type: "Polygon",
        style: this.defaultStyle
      });

      this.drawInteraction.on("drawstart", () => {
        this.featureIsDrawn = true;
      });

      this.drawInteraction.on("drawend", event => {
        this.updateMapFeature(event.feature);
        this.$nextTick(() => {
          this.centerMap();
          this.snapSource.addFeature(event.feature);
          this.mapHasFeatures = true;
        });
        this.drawInteraction.setActive(false);
        this.modifyInteraction.setActive(true);
      });

      this.map.addInteraction(this.drawInteraction);
      this.drawInteraction.setActive(false);
    },

    createModifyInteraction() {
      this.modifyInteraction = new Modify({
        source: this.vectorSource
      });

      this.modifyInteraction.on("modifyend", event => {
        this.updateMapFeature(event.features.item(0));
      });

      this.map.addInteraction(this.modifyInteraction);
      this.modifyInteraction.setActive(false);
    },

    createSnapInteraction() {
      this.snapSource = new VectorSource({ wrapX: false });
      this.snap = new Snap({
        source: this.snapSource
      });
      this.map.addInteraction(this.snap);
    },

    /**
     * Zooms in on the map by 1 with an animation duration of 250 milliseconds
     */
    zoomIn() {
      let view = this.map.getView();
      let zoom = view.getZoom();
      view.animate({ zoom: zoom + 1, duration: 250 });
    },

    /**
     * Zooms out on the map by 1 with an animation duration of 250 milliseconds
     */
    zoomOut() {
      let view = this.map.getView();
      let zoom = view.getZoom();
      view.animate({ zoom: zoom -1, duration: 250 });
    },

    updateMapFeature(feature) {
      this.userDrawnFeature = feature;
      let geoJsonFormatter = new GeoJSON();
      let featureGeoJSON = geoJsonFormatter.writeFeature(feature);
      this.featureWKT = new WKT().writeFeature(
        geoJsonFormatter.readFeature(featureGeoJSON)
      );
      this.$emit("feature-wkt", this.featureWKT);
    },

    /**
     * Creates the layer where the Center on Coordinates pin is placed
     */
    createPointLayer() {
      this.pointLayerSource = new VectorSource({ wrapX: false });
      this.pointLayer = new VectorLayer({
        source: this.pointLayerSource
      });
      this.map.addLayer(this.pointLayer);
    },

    /**
     * Centers the map
     * If the user has drawn a feature, the map is centered on it, else the map is centered on the non-drawn features
     */
    centerMap() {
      if (this.mapHasFeatures) {
        if(this.userDrawnFeature){
          let featureExtent = this.userDrawnFeature.getGeometry();
          this.map.getView().fit(featureExtent, this.map.getSize());
        } else {
          this.zoomOnFeatures();
        }
      }
    },

    /**
     * Clears the drawn polygon
     */
    resetPolygon() {
      if(this.isDeleteLayers){
        this.$emit('map-restart');
        return;
      }
      this.modifyInteraction.setActive(false);
      //it's necessary to delete the polygon in the middle of the drawing
      this.drawInteraction.finishDrawing();
      this.vectorLayer.getSource().clear(); // clears the layer where the polygon was drawn
      this.drawInteraction.setActive(true);
      this.userDrawnFeature = null;
      this.featureIsDrawn = false;
      this.pointLayer.getSource().clear(); // clears the layer where the location pin is placed
    },

    openCoordinatesInputs() {
      this.showCoordinatesInputs = true;
    },

    closeCoordinatesInputs() {
      this.showCoordinatesInputs = false;
      this.coordinates.latitude = null;
      this.coordinates.longitude = null;
    },

    /**
     * Centers the map on the coordinates input by the user
     */
    centerOnCoordinates() {
      if (this.coordinatesAreValid()) {
        this.pointLayerSource.clear(); // clears the layer of any feature
        let feature = this.markPointFeature.clone(); // creates a copy of the location pin feature
        feature
          .getGeometry()
          .setCoordinates(
            transform(
              [
                parseFloat(this.coordinates.longitude),
                parseFloat(this.coordinates.latitude)
              ],
              "EPSG:4326",
              "EPSG:3857"
            )
          );
        this.pointLayerSource.addFeature(feature); // adds the location pin feature
        this.mapHasFeatures = true;

        this.mapView.setCenter( // centers the map on the coordinates input by the user
          transform(
            [
              parseFloat(this.coordinates.longitude),
              parseFloat(this.coordinates.latitude)
            ],
            "EPSG:4326",
            "EPSG:3857"
          )
        );

        let point = feature.getGeometry();
        this.mapView.fit(point, {
          minResolution: 100
        });
        this.closeCoordinatesInputs();
      }
    },

    /**
     * Checks if the user input coordinates are valid
     */
    coordinatesAreValid() {
      if (!this.validLatitude() && !this.validLongitude()) {
        this.$toasted.global.toast_error(this.$i18n.t('ValidLatitudeAndLongitude'))
      } else if (!this.validLatitude()) {
        this.$toasted.global.toast_error(this.$i18n.t('ValidLatitude'))
      } else if (!this.validLongitude()) {
        this.$toasted.global.toast_error(this.$i18n.t('ValidLongitude'))
      }
      return this.validLatitude() && this.validLongitude();
    },

    /**
     * Checks if the latitude input by the user is between -90 and 90
     */
    validLatitude() {
      if (this.coordinates.latitude !== null) {
        let latitude = parseFloat(this.coordinates.latitude);
        return latitude >= -90 && latitude <= 90
      } else {
        return false;
      }
    },

    /**
     * Checks if the longitude input by the user is between -180 and 180
     */
    validLongitude() {
      if (this.coordinates.longitude !== null) {
        let longitude = parseFloat(this.coordinates.longitude);
        return longitude >= -180 && longitude <= 180;
      } else {
        return false;
      }
    },

    openUploadGeomFileDialog() {
      this.uploadGeomFileDialogIsOpen = true
    },

    closeUploadGeomFileDialog() {
      this.uploadGeomFileDialogIsOpen = false;
      this.file = null;
      this.fileProjection = null;
      this.fileType = null;
      this.$refs.fileInput.$refs.dropFileInput.trashFile(); // executes the method in the input field component to clear the file info
    },

    /**
     * Imports the features in the input file, adds it to the map and centers it on the feature
     */
    async submitForm() {
      try{
        this.loading = true;
        this.vectorLayer.getSource().clear(); // clears the layer of the features drawn by the user

        let file = this.file;
        if (!file) {
          return;
        }

        const response =  await this.CREATE_IMPORT_VALIDATION({
          endpoint: "workspaces",
          data: this.getFormData(file)
        });
        
        this.$emit('set-shape-geom', response[0].geom);
        
        this.addMapFeature(response[0].wp_geo_json);
        this.updateMapFeature(this.userDrawnFeature);
        this.centerMap();
        this.featureIsDrawn = true;
        this.closeUploadGeomFileDialog();
        
      }catch(error){
        this.$toasted.global.toast_error(error.response.data.message);
      }finally{
        this.loading = false;
      }
    },

    drawWorkspace(geo_json){

      this.vectorLayer.getSource().clear();
      if(!geo_json){
        return
      }
      this.addMapFeature(geo_json);
      this.updateMapFeature(this.userDrawnFeature);
      this.centerMap();
      this.featureIsDrawn = true;
      
    },

    drawWorkspaces(geo_json){
      if(!geo_json){
        return
      }
      this.addMapFeatures(geo_json);
    },

    addMapFeatures(workspaceGeoJSON, isPolygon) {
      let feature = this.readFeatureFromGeoJSON(workspaceGeoJSON, isPolygon);
      this.vectorSource.addFeature(feature);
      this.mapHasFeatures = true;
    },

    getFormData(file) {
      let formData = new FormData();
      formData.append("file", file);
      formData.append("projection", this.fileProjection);
      formData.append("file_type", this.fileType);
      formData.append("get_geom", true);
      return formData;
    },

    /**
     * Adds a feature to the map
     * @param {String} workspaceGeoJSON a string containing the feature GeoJSON
     * @param {*} isPolygon
     */
    addMapFeature(workspaceGeoJSON, isPolygon) {
      let feature = this.readFeatureFromGeoJSON(workspaceGeoJSON, isPolygon);
      this.vectorSource.clear();
      this.vectorSource.addFeature(feature);
      if (this.drawable) {
        this.snapSource.addFeature(feature);
      }
      this.mapHasFeatures = true;
    },

    /**
     * Reads a feature from a geoJSON string
     * @param {String} workspaceGeoJSON string containing a geoJSON
     * @param {Boolean} isPolygon indicates if the geoJSON is a polygon
     */
    readFeatureFromGeoJSON(workspaceGeoJSON, isPolygon) {
      if (workspaceGeoJSON) {
        let features = new GeoJSON().readFeatures(workspaceGeoJSON, {
          featureProjection: isPolygon ? "EPSG:4326" : "EPSG:3857",
        });
        this.userDrawnFeature = features[0] // remover
        return features[0];
      }
    },

    async loadProjections() {
      try {
        let projectionOptions = [];
        let projections = await this.GET_LIST({ listName: "list_projections", code: this.fileProjection });
        projections.forEach(projection => {
          projectionOptions.push({
            id: projection.code,
            label: projection.code + " : " + projection.definition
          });
        });
        this.fileProjectionOptions = projectionOptions;
      } catch (error) {
        let errorHandle = utils.errorHandler(error);
        if (errorHandle && typeof errorHandle === "string") {
          this.$toasted.global.toast_error(errorHandle);
        }
      }
    },

    addFeaturesToPointLayer(geoJSONArray) {
      this.pointLayerSource.clear();
      geoJSONArray.forEach(geoJSONString => {
        let feature = new GeoJSON().readFeature(geoJSONString, {
          featureProjection: "EPSG:3857",
        })
        feature.setStyle(
          this.getFeatureStyle(feature.getGeometry().getType(), this.mapFeaturesStyle)
        )
        this.pointLayerSource.addFeature(feature)
      });

      this.mapHasFeatures = true;
      this.zoomOnFeatures();
    },

    clearVectorSource(){
      this.vectorSource.clear();
      this.featureIsDrawn = false;
    },

    showHideAllWorkspacesMethod(){
      this.showAllWorkspaces = !this.showAllWorkspaces;
      if(this.showAllWorkspaces){
        this.listOfWorkspaces.forEach(element => {
          this.drawWorkspaces(element.wp_geo_json);
          this.updateMapFeature(this.userDrawnFeature);
          this.featureIsDrawn = true;
      });
      }else{
        this.resetPolygon();
      }
    },

    zoomOutMap(){
      if(this.mapHasFeatures){
        let view = this.map.getView();
        view.animate({ zoom: 5, duration: 250 });
        this.mapHasFeatures = false;
      }
    },

    clearMapHasFeatures(){
      this.mapHasFeatures = false;
    },

    hightlightAddFeaturesToPointLayer(items) {
      //if empty array clear selectedIds array
      this.pointLayerSource.clear();
      if(items.length === 0){
        this.selectedIds = [];
        return;
      }
      items.forEach(item => {
        let feature = new GeoJSON().readFeature(item.geo_json, {
          featureProjection: "EPSG:3857",
        })
        feature.setStyle(
          this.getFeatureStyle(feature.getGeometry().getType(), this.mapFeaturesStyle)
        )
        // Set ID to the feature
        feature.setId(item.id);
        if(this.selectedIds.includes(item.id)){
          const existingStyle = feature.getStyle();
          let style = Array.isArray(existingStyle) ? existingStyle[0] : existingStyle;
          if (style) {
            // Modify the fill color of the existing style
            const image = style.getImage();
            if(image){
              // Define a new style for highlighted features
              style = new Style({
                image: new Circle({
                  radius: 15,  // or whatever radius you need
                  fill: new Fill({
                    color: 'var(--app-primary-base)',
                    zIndex: 1,
                  }),
                }),
              });
            }

            // Modify the fill color of the existing style
            const fill = style.getFill();
            if (fill) {
              fill.setColor('var(--app-primary-base)');
            }

            // Modify the stroke color of the existing style
            const stroke = style.getStroke();
            if (stroke) {
              stroke.setColor('var(--app-primary-base)');
            }
            // Apply the modified style to the feature
            feature.setStyle(style);
          }
        }
        this.pointLayerSource.addFeature(feature)
      });

      this.mapHasFeatures = true;
      this.zoomOnFeatures();
    },

    highlightFeatures(ids) {
      // Iterate through each feature in the point layer
      this.pointLayerSource.getFeatures().forEach(feature => {
        const featureId = feature.getId();
        // Check if the feature ID is in the array of IDs to highlight
        if (featureId !== null && ids.includes(featureId)) {
          if (!this.selectedIds.includes(featureId)) {
            this.selectedIds.push(featureId);
          }
          // Style for highlighted feature
          const existingStyle = feature.getStyle();
          let style = Array.isArray(existingStyle) ? existingStyle[0] : existingStyle;
          if (style) {
            // Modify the fill color of the existing style
            const image = style.getImage();
            if(image){
              // Define a new style for highlighted features
              style = new Style({
                image: new Circle({
                  radius: 15,  // or whatever radius you need
                  fill: new Fill({
                    color: 'var(--app-primary-base)',
                    zIndex: 1,
                  }),
                }),
              });
            }

            // Modify the fill color of the existing style
            const fill = style.getFill();
            if (fill) {
              fill.setColor('var(--app-primary-base)');
            }

            // Modify the stroke color of the existing style
            const stroke = style.getStroke();
            if (stroke) {
              stroke.setColor('var(--app-primary-base)');
            }
            // Apply the modified style to the feature
            feature.setStyle(style);
          }
        } else {
          // Remove the feature ID from selectedIds array
          const index = this.selectedIds.indexOf(featureId);
          if (index !== -1) {
            this.selectedIds.splice(index, 1);
          }
          // Default style for other features
          feature.setStyle(this.getFeatureStyle(feature.getGeometry().getType(), this.mapFeaturesStyle));
        }
      });
    },

    clearSelectedIds(){
      this.selectedIds = [];
    },

    /**
     * Zooms on the non-drawn features
     */
    zoomOnFeatures() {
      let extent = this.pointLayer.getSource().getExtent();
      this.map.getView().fit(extent, this.map.getSize());
    },
  }
};
</script>

<style scoped>
.management-map-buttons {
  position: absolute;
  width: fit-content;
  height: fit-content;
  display: flex;
  flex-direction: column;
  gap: 4px;
  left: 6px;
  top: 6px;

  z-index: 5;
  justify-content: space-between;
  align-items: flex-start;
}

.buttons-container {
  background-color: #FFFFFF;
  border-radius: 4px;
  gap: 2px;
  display: flex;
  flex-direction: column;
  padding: 5px;
}

.buttons-container >>> div {
  line-height: 10px;
}

.coordinates-inputs-container {
  display: flex;
  flex-direction: row;
  gap: 4px;
  background-color: var(--app-primary-base);
  border-radius: 4px;
  padding: 4px;
}

.buttons-container-custom {
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
}

#dropzone {
  margin-top: 20px;
  min-height: 100px;
}

.management-workspace-map {
  position: relative;
  width: 100%;
  border-radius: 8px;
}

.theme--light.v-btn.v-btn--disabled:not(.v-btn--icon):not(.v-btn--text):not(.v-btn--outline).aerial {
  background-color: rgba(255, 255, 255, 0.5) !important;
}

.theme--light.v-btn.v-btn--disabled.aerial,
.theme--light.v-btn.v-btn--disabled.aerial .v-icon {
  color: rgb(0 0 0 / 50%) !important;
}

/**
 * checkbox color when not selected
 */
.management-workspace-map .v-input {
  color: white;
}

/**
 * all workspaces button style
 */
.all-workspaces-button {
  z-index: 5;
  position: absolute;
  top: 8px;
}
.npv-class{
  width: 100%;
  height: 100%;
}

</style>



// WEBPACK FOOTER //
// src/newComponents/_Molecules/ManagementMap.vue